import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { size, trim } from 'lodash';
import moment from 'moment';

import { NumberField } from 'containers/Form/fields';

import useClickOusideContainer from 'custom-hooks/useClickOusideContainer';
import useMutableState from 'custom-hooks/useMutableState';

import { accessApi } from 'utils/injectApi';
import { cep, cpfCnpj, isCNPJ, isCPF, removeDiacritics } from 'utils/tools';

import Calendario, { disabledPast } from './Calendario';
import ErrorMessages, { errorsHasType } from './ErrorMessages';
import ShowDebug from './ShowDebug';

const BoletoForm = ({
	containerRef,
	boleto,
	dadosRequerimento,
	boletoGerado,
	onCancelHandler,
	onSubmitHandler,
	readOnly,
	dataVencimentoInicial,
	vencimentoEditavel,
	instrucaoMaxLength,
	nomeTaxa,
	correcaoGeracaoAutomatica,
	children,
	usuarioInterno = true
}) => {
	const outside = useClickOusideContainer(containerRef);
	const [errors, setErrors] = useState();
	const [data, setData] = useState(boleto || { dataVencimento: dataVencimentoInicial });
	const [dadosCopiados, setDadosCopiados] = useState(false);
	const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
	const formulario = useMutableState(['licenciamento', 'formulario']);

	useEffect(() => {
		if (boleto) {
			setData(boleto);
		}
	}, [boleto]);

	const submit = useCallback(onSubmitHandler, [onSubmitHandler]);
	const cancel = useCallback(onCancelHandler, [onCancelHandler]);
	const [isEstadoAlterado, setIsEstadoAlterado] = useState(false);

	const setBoletoHandler = useCallback(
		(value, name) => {
			let auxValue = value;
			if (name === 'cpfSacado') {
				auxValue = cpfCnpj(value);
			} else if (name === 'cepSacado') {
				auxValue = cep(value);
			} else if (name.startsWith('dataVencimento')) {
				if (moment.isMoment(value)) {
					auxValue = auxValue.format('DDMMYYYY');
				}
			} else if (name === 'ufSacado') {
				auxValue = auxValue.toUpperCase();
			} else if (name === 'enderecoSacado') {
				auxValue = removeDiacritics(auxValue);
				auxValue = auxValue.replace('º', '.').replace('ª', '.');
				auxValue = auxValue.replace(/[^a-zA-Z0-9 /.\-@#$%&]/g, '');
			}
			setData(oldState => {
				const newState = { ...oldState, [name]: auxValue };

				setIsEstadoAlterado(true);
				return newState;
			});

			if (errors?.[name]) {
				let errorsAux = errors;
				delete errorsAux[name];
				setErrors(errorsAux);
			}
		},
		[errors, setErrors]
	);

	const submitHandler = useCallback(
		({ data: dataSubmit, gerarBoleto, validate, keepOpen, draft }) => {
			const errors = {};
			const dados = { ...dataSubmit };

			if (size(trim(dados?.dataVencimento)) === 0 || typeof dados?.dataVencimento !== 'string') {
				errors.dataVencimento = ['Campo obrigatório'];
			} else {
				if (!boletoGerado) {
					if (moment(dados.dataVencimento, 'DDMMYYYY').isBefore(moment().startOf('day'))) {
						errors.dataVencimento = ['Data não pode estar no passado'];
					}
				}
			}

			if (parseFloat(dados.valor || 0) === 0) {
				errors.valor = ['Você deve informar o valor para a Taxa informada'];
			}
			if (size(trim(dados?.nomeSacado)) === 0) {
				errors.nomeSacado = ['Campo obrigatório'];
			}
			if (size(trim(dados?.cpfSacado)) === 0) {
				errors.cpfSacado = ['Campo obrigatório'];
			} else if (!isCPF(dados?.cpfSacado) && !isCNPJ(dados?.cpfSacado)) {
				errors.cpfSacado = ['Documento deve ser um CPF ou CNPJ válidos'];
			}
			if (size(trim(dados?.enderecoSacado)) === 0) {
				errors.enderecoSacado = ['Campo obrigatório'];
			}
			if (size(trim(dados?.cepSacado)) === 0) {
				errors.cepSacado = ['Campo obrigatório'];
			}
			if (size(trim(dados?.bairroSacado)) === 0) {
				errors.bairroSacado = ['Campo obrigatório'];
			}
			if (size(trim(dados?.ufSacado)) === 0) {
				errors.ufSacado = ['Campo obrigatório'];
			}
			if (size(trim(dados?.municipioSacado)) === 0) {
				errors.municipioSacado = ['Campo obrigatório'];
			}
			if (size(errors) > 0 && validate) {
				setErrors(errors);
			} else {
				dados.nomeSacado = removeDiacritics(dados.nomeSacado);
				dados.enderecoSacado = removeDiacritics(dados.enderecoSacado);
				dados.bairroSacado = removeDiacritics(dados.bairroSacado);
				dados.ufSacado = removeDiacritics(dados.ufSacado);
				dados.municipioSacado = removeDiacritics(dados.municipioSacado);
				dados.instrucao1 = removeDiacritics(dados.instrucao1);
				dados.instrucao2 = removeDiacritics(dados.instrucao2);
				dados.instrucao3 = removeDiacritics(dados.instrucao3);
				dados.instrucao4 = removeDiacritics(dados.instrucao4);

				dados.situacaoDadosBoleto = size(errors) === 0 ? 'completo' : 'incompleto';
				submit && submit(dados, gerarBoleto, validate, keepOpen, draft);
				setIsEstadoAlterado(false);
				setErrors({});
			}
		},
		[boletoGerado, submit]
	);

	useEffect(() => {
		if (outside && usuarioInterno) {
			if (isEstadoAlterado) {
				submitHandler({ data, gerarBoleto: false, validate: false, keepOpen: true });
			}
		}
	}, [data, isEstadoAlterado, outside, submitHandler, usuarioInterno]);

	const getDateValue = useCallback(
		dataBol => (typeof dataBol === 'string' ? moment(dataBol, 'DDMMYYYY') : dataBol),
		[]
	);

	const copiarDadosRequerimento = useCallback(() => {
		const dataAux = { ...data };
		if (dataAux) {
			Object.keys(dataAux).forEach(key => {
				if (size(dataAux[key]) === 0) {
					delete dataAux[key];
				}
			});
		}
		const boleto = {
			...dadosRequerimento,
			...(dataAux || {})
		};
		setData(boleto);
	}, [dadosRequerimento, data]);

	const tudoGerado = useMemo(() => {
		let tudoGerado = true;

		if (!boletoGerado) {
			tudoGerado = false;
		}

		return tudoGerado;
	}, [boletoGerado]);

	// Effect para copiar os dados do requerimento para o Boleto - Apenas PORTAL
	useEffect(() => {
		if (
			size(data?.valor) > 0 &&
			size(dadosRequerimento) > 0 &&
			size(dadosRequerimento.nomeSacado) > 0 &&
			!usuarioInterno &&
			!dadosCopiados
		) {
			copiarDadosRequerimento();
			setDadosCopiados(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, dadosRequerimento, usuarioInterno]);

	const handleRemoveData = async () => {
		setShowRemoveConfirmation(false);
		try {
			const url = `/sceb/excluir-boleto/${formulario.formData.id}`;
			await accessApi(url, false, 'get');
			setData({});
			setErrors({});
		} catch (err) {
			console.error('Erro ao remover dados do boleto:', err);
		}
	};

	return (
		<form autoComplete="off" noValidate className="mb-2">
			<>
				{!readOnly && <h3 style={{ margin: '0' }}>Dados para geração do Boleto no SCEB</h3>}
				{!readOnly && usuarioInterno && (
					<p style={{ marginLeft: '14px', fontSize: '10pt', marginTop: '10pt' }}>
						<b>Obs:</b> qualquer caractere acentuado será removido para geração dos boletos
					</p>
				)}

				{(!boletoGerado || readOnly) && (
					<>
						<div className="col-md-12 titulos-geracao-boletos">{nomeTaxa.toUpperCase()}</div>
						<div style={{ display: 'flex', alignItems: 'top' }}>
							<div className="col-md-6">
								<label className="boleto-label">
									<span>Vencimento *</span>
								</label>
								<Calendario
									value={getDateValue(data?.dataVencimento)}
									onChange={date => setBoletoHandler(date, 'dataVencimento')}
									disabledDate={disabledPast}
									readOnly={!usuarioInterno || readOnly || !vencimentoEditavel}
									disabled={!usuarioInterno || readOnly || !vencimentoEditavel}
								/>
								{size(errors?.dataVencimento) > 0 && <ErrorMessages errorList={errors.dataVencimento} />}
							</div>
							<div className="col-md-6">
								<label className="boleto-label">Valor</label>
								<NumberField
									name={'valor'}
									value={data?.valor ? { value: data.valor } : {}}
									required={false}
									decimals={2}
									onChangeHandler={([valid]) => setBoletoHandler(valid.value.value, valid.name)}
									readOnly={!usuarioInterno || readOnly || correcaoGeracaoAutomatica}
									disabled={!usuarioInterno || readOnly || correcaoGeracaoAutomatica}
									maxSize="10"
									placeHolder={'valor taxa'}
								/>
								{size(errors?.valor) > 0 && <ErrorMessages errorList={errors.valor} />}
							</div>
						</div>
						<Instrucoes
							label={nomeTaxa}
							data={data}
							maxLength={instrucaoMaxLength}
							errors={errors?.instrucao || []}
							onChangeHandler={setBoletoHandler}
							readOnly={!usuarioInterno || readOnly}
						/>
					</>
				)}
				<div className="sacado-wrapper">
					<div className="col-md-12" style={{ marginBottom: '20px' }}>
						<h3 className="titulo-sacado">DADOS DO SACADO</h3>
						{!readOnly && usuarioInterno && (
							<button type="button" className="btn btn-default" onClick={copiarDadosRequerimento}>
								<span>Copiar dados do requerimento</span>
							</button>
						)}
					</div>

					{size(errors?.valor) > 0 && <ErrorMessages errorList={errors.valor} />}
					<div className="col-md-6">
						<label>Nome do sacado *</label>
						<input
							type="text"
							name={'nomeSacado'}
							value={data?.nomeSacado || ''}
							required={false}
							onChange={e => setBoletoHandler(e.target.value, 'nomeSacado')}
							readOnly={readOnly || tudoGerado}
							disabled={readOnly || tudoGerado}
							placeholder={'nome do sacado'}
						/>
						{size(errors?.nomeSacado) > 0 && <ErrorMessages errorList={errors.nomeSacado} />}
					</div>
					<div className="col-md-6">
						<label>CPF/CNPJ do sacado *</label>
						<input
							type="text"
							name={'cpfSacado'}
							value={data?.cpfSacado || ''}
							required={false}
							onChange={e => setBoletoHandler(e.target.value, 'cpfSacado')}
							readOnly={readOnly || tudoGerado}
							disabled={readOnly || tudoGerado}
							placeholder={'cpf/cnpj do sacado'}
						/>
						{size(errors?.cpfSacado) > 0 && <ErrorMessages errorList={errors.cpfSacado} />}
					</div>
					<div className="col-md-12">
						<label>Endereço *</label>
						<input
							type="text"
							name={'enderecoSacado'}
							value={data?.enderecoSacado || ''}
							required={false}
							onChange={e => setBoletoHandler(e.target.value, 'enderecoSacado')}
							readOnly={readOnly || tudoGerado}
							disabled={readOnly || tudoGerado}
							placeholder={'endereço do sacado'}
						/>
						{size(errors?.enderecoSacado) > 0 && <ErrorMessages errorList={errors.enderecoSacado} />}
					</div>
					<div className="col-md-6">
						<label>CEP *</label>
						<input
							type="text"
							name={'cepSacado'}
							value={data?.cepSacado || ''}
							required={false}
							onChange={e => setBoletoHandler(e.target.value, 'cepSacado')}
							readOnly={readOnly || tudoGerado}
							disabled={readOnly || tudoGerado}
							placeholder={'cep do sacado'}
						/>
						{size(errors?.cepSacado) > 0 && <ErrorMessages errorList={errors.cepSacado} />}
					</div>
					<div className="col-md-6">
						<label>Bairro *</label>
						<input
							type="text"
							name={'bairroSacado'}
							value={data?.bairroSacado || ''}
							required={false}
							onChange={e => setBoletoHandler(e.target.value, 'bairroSacado')}
							readOnly={readOnly || tudoGerado}
							disabled={readOnly || tudoGerado}
							placeholder={'bairro do sacado'}
						/>
						{size(errors?.bairroSacado) > 0 && <ErrorMessages errorList={errors.bairroSacado} />}
					</div>
					<div className="col-md-6">
						<label>UF *</label>
						<input
							type="text"
							name={'ufSacado'}
							value={data?.ufSacado || ''}
							required={false}
							onChange={e => setBoletoHandler(e.target.value, 'ufSacado')}
							readOnly={readOnly || tudoGerado}
							disabled={readOnly || tudoGerado}
							placeholder={'uf do sacado'}
							maxLength={2}
						/>
						{size(errors?.ufSacado) > 0 && <ErrorMessages errorList={errors.ufSacado} />}
					</div>
					<div className="col-md-6">
						<label>Município *</label>
						<input
							type="text"
							name={'municipioSacado'}
							value={data?.municipioSacado || ''}
							required={false}
							onChange={e => setBoletoHandler(e.target.value, 'municipioSacado')}
							readOnly={readOnly || tudoGerado}
							disabled={readOnly || tudoGerado}
							placeholder={'municipio do sacado'}
						/>
						{size(errors?.municipioSacado) > 0 && <ErrorMessages errorList={errors.municipioSacado} />}
					</div>
				</div>
			</>
			{size(errors?.boleto) > 0 && <ErrorMessages errorList={errors.boleto} />}
			{!readOnly && (
				<>
					<div className="submit-group-multiline">
						<button
							type="button"
							className="btn btn-default"
							onClick={() => submitHandler({ data, gerarBoleto: false, validate: false, keepOpen: true, draft: true })}
						>
							<span>Salvar estado atual</span>
							<i className="fa fa-file-text-o"></i>
						</button>
						{!correcaoGeracaoAutomatica && (
							<button type="button" className="btn btn-default" onClick={cancel}>
								<span>Cancelar</span>
							</button>
						)}
						{/* NOVO BOTÃO: REMOVER OS DADOS */}
						{usuarioInterno && (
							<button
								type="button"
								className="btn btn-danger"
								style={{ marginLeft: '8px' }}
								onClick={() => setShowRemoveConfirmation(true)}
								disabled={showRemoveConfirmation}
							>
								Remover os dados
							</button>
						)}
						{children}
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => submitHandler({ data, gerarBoleto: true, validate: true, keepOpen: false, draft: false })}
						>
							<span>Gerar Boleto</span>
						</button>
					</div>

					{/* MENSAGEM E BOTÃO DE CONFIRMAÇÃO DA REMOÇÃO */}
					{showRemoveConfirmation && (
						<div style={{ marginTop: '15px', padding: '10px', border: '1px solid #ccc' }}>
							<p>
								Este botão remove todos os dados de boleto que você inseriu, permitindo responder a consulta sem gerar
								os boletos.
								<br />
								Você confirma esta limpeza de dados?
							</p>
							<div className="submit-group-multiline">
								<button type="button" className="btn btn-default" onClick={() => setShowRemoveConfirmation(false)}>
									Cancelar
								</button>
								<button type="button" className="btn btn-danger" onClick={handleRemoveData}>
									Confirmar limpeza de dados
								</button>
							</div>
						</div>
					)}

					{size(errors) > 0 &&
						errorsHasType(errors, 'error') &&
						(console.debug('errors', errors) || (
							<ErrorMessages errorList={['Há erros no preenchimento do formulário. Verifique acima.']} />
						))}
				</>
			)}
			<ShowDebug data={data} />
		</form>
	);
};

BoletoForm.displayName = 'BoletoForm';
BoletoForm.propTypes = {
	containerRef: PropTypes.any,
	boleto: PropTypes.object,
	dadosRequerimento: PropTypes.object,
	boletoGerado: PropTypes.bool,
	readOnly: PropTypes.bool,
	dataVencimentoInicial: PropTypes.string,
	vencimentoEditavel: PropTypes.bool,
	instrucaoMaxLength: PropTypes.number,
	nomeTaxa: PropTypes.string,
	correcaoGeracaoAutomatica: PropTypes.bool,
	onSubmitHandler: PropTypes.func,
	onCancelHandler: PropTypes.func,
	onChangeHandler: PropTypes.func,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	style: PropTypes.object,
	usuarioInterno: PropTypes.bool
};

export default BoletoForm;

function Instrucoes({ label, maxLength, data, errors, onChangeHandler, readOnly }) {
	const property1 = 'instrucao1';
	const property2 = 'instrucao2';
	const property3 = 'instrucao3';
	const property4 = 'instrucao4';
	return (
		<div className="col-md-12 box-instrucoes-tipo-boleto">
			<label>Instruções</label>
			<input
				className="form-control"
				name={property1}
				placeholder={`Linha 1 de instruções para ${label} (até ${maxLength} caracteres)`}
				value={data?.[property1] || ''}
				onChange={e => onChangeHandler(e.target.value, property1)}
				maxLength={maxLength}
				readOnly={readOnly}
				disabled={readOnly}
			/>
			<input
				className="form-control"
				name={property2}
				placeholder={`Linha 2 de instruções para ${label} (até ${maxLength} caracteres)`}
				value={data?.[property2] || ''}
				onChange={e => onChangeHandler(e.target.value, property2)}
				maxLength={maxLength}
				readOnly={readOnly}
				disabled={readOnly}
			/>
			<input
				className="form-control"
				name={property3}
				placeholder={`Linha 3 de instruções para ${label} (até ${maxLength} caracteres)`}
				value={data?.[property3] || ''}
				onChange={e => onChangeHandler(e.target.value, property3)}
				maxLength={maxLength}
				readOnly={readOnly}
				disabled={readOnly}
			/>
			<input
				className="form-control"
				name={property4}
				placeholder={`Linha 4 de instruções para ${label} (até ${maxLength} caracteres)`}
				value={data?.[property4] || ''}
				onChange={e => onChangeHandler(e.target.value, property4)}
				maxLength={maxLength}
				readOnly={readOnly}
				disabled={readOnly}
			/>
			{size(errors) > 0 && <ErrorMessages errorList={errors} />}
		</div>
	);
}
Instrucoes.displayName = 'Instruções';
Instrucoes.propTypes = {
	label: PropTypes.string,
	maxLength: PropTypes.number,
	data: PropTypes.object,
	errors: PropTypes.arrayOf(PropTypes.string),
	onChangeHandler: PropTypes.func,
	readOnly: PropTypes.bool,
	usuarioInterno: PropTypes.bool
};
